import { AppRoutes } from '../../common/constants';
import { Box, Card, CardContent, Tab, Tabs } from '@mui/material';
import { buildFnaParties } from '../hooks/useFnaPartiesUpdater';
import { ClientType } from '../../common/types/client';
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SAVED_MESSAGE } from '../../common/lib/fulcrumApi';
import { FNADetails, NeedCapacityType } from '../../common/types/analyses';
import { JSX, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUpdateEffect } from '../../common/hooks/useUpdateEffect';
import { useUpdateFna } from '../../common/components/mutations';
import ClientsStep from './steps/clientsStep';
import clsx from 'clsx';
import Content from '../../common/components/content';
import differenceBy from 'lodash/differenceBy';
import FnaDetailsStep from './steps/fnaDetailsStep';
import FnaPageHeaderSummary from '../components/fnaPageHeaderSummary';
import Layout from '../../common/components/layout';
import LayoutLoadingState from '../../common/components/layout/layoutLoadingState';
import NeedsStep from './steps/needsStep';
import PartiesStep from './steps/partiesStep';
import StepFooter from '../../common/components/stepFooter';

const LAST_STEP_INDEX = 3;

/**
 * Edit FNA page
 */
const EditFnaPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const [searchParameters] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParameters.has('tab') ? Number(searchParameters.get('tab')) : 0, // get default tab from URL query parameter "tab"
  );
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateFna();
  const { data: fna } = useQuery<FNADetails>({
    queryKey: [`/FNADetail/${id}`],
    enabled: Boolean(id),
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
    setValue,
  } = useForm<FNADetails>({
    defaultValues: fna,
  });

  const commitFnaChanges = async (): Promise<void> => {
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: FNADetails): Promise<void> => {
    try {
      const updatedFna = await mutateAsync(data);
      const newParties = differenceBy(updatedFna.parties, fna?.parties ?? [], 'id');

      if (newParties.length > 0) {
        // Add new parties to keypersonParties, suretyshipParties and buyandsellParties
        await mutateAsync({
          ...updatedFna,
          keypersonParties:
            data.keypersonParties?.length > 0
              ? [...data.keypersonParties, ...buildFnaParties(newParties)]
              : undefined,
          suretyshipParties:
            data.suretyshipParties?.length > 0
              ? [...data.suretyshipParties, ...buildFnaParties(newParties)]
              : undefined,
          buyandsellParties:
            data.buyandsellParties?.length > 0
              ? [
                  ...data.buyandsellParties,
                  ...buildFnaParties(newParties, {
                    fnaBuyandsellID: data.buyandsell?.id,
                    capacityID: NeedCapacityType.BuyingAndSelling,
                  }),
                ]
              : undefined,
        });
      }
      enqueueSnackbar(DEFAULT_SAVED_MESSAGE, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar((error as Error).message || DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  };

  const handleSaveAndExit = (): void => {
    commitFnaChanges().then(() => {
      navigate(AppRoutes.FNAs);
    });
  };

  // If there are more steps, go to the next one (otherwise save and go to FNAs page)
  const handleClickNext = (): void => {
    if (
      activeTab === 2 &&
      fna?.parties?.some(
        ({ partyRoleplayerTypeID, representativeRoleplayerID }) =>
          partyRoleplayerTypeID === ClientType.Business && !representativeRoleplayerID,
      )
    ) {
      enqueueSnackbar('Please choose a representative life for the legal entity', {
        variant: 'error',
      });
      return;
    }

    if (activeTab < LAST_STEP_INDEX) {
      setActiveTab((value) => value + 1);
    } else {
      handleSaveAndExit();
    }
  };

  // Reset form when fna object changes
  useEffect(() => reset(fna), [fna, reset]);

  // Submit form (save changes) when active tab changes
  useUpdateEffect(() => {
    if (isDirty) {
      commitFnaChanges();
    }
  }, [activeTab]);

  if (!fna) {
    return <LayoutLoadingState />;
  }

  const fnaRoleplayers =
    fna.roleplayers.map(({ roleplayerName }) => roleplayerName).join(', ') || '-';
  const breadcrumbs = [
    { label: 'Financial needs analyses', href: AppRoutes.FNAs },
    { label: fnaRoleplayers, href: `${AppRoutes.FNAs}/${fna.id}` },
  ];

  const stepProps = {
    fna,
    control,
    setValue,
    commitFnaChanges,
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <Box className="md:flex justify-between items-start md:space-x-4 space-y-8 md:space-y-0">
          <FnaPageHeaderSummary fna={fna} />
          <LoadingButton
            className="flex-shrink-0"
            onClick={handleSaveAndExit}
            loading={mutationIsLoading}
            variant="contained"
            color="primary"
          >
            Save and exit
          </LoadingButton>
        </Box>
        <Tabs
          className="mt-12"
          variant="scrollable"
          scrollButtons="auto"
          value={activeTab}
          onChange={(_, newValue: number): void => setActiveTab(newValue)}
        >
          <Tab label="FNA details" />
          <Tab label="Client" />
          <Tab label="Parties" />
          <Tab label="Needs" />
        </Tabs>
        <Card>
          <CardContent className={clsx(mutationIsLoading && 'animate-pulse')}>
            {activeTab === 0 && <FnaDetailsStep {...stepProps} />}
            {activeTab === 1 && <ClientsStep {...stepProps} />}
            {activeTab === 2 && <PartiesStep {...stepProps} />}
            {activeTab === 3 && <NeedsStep {...stepProps} />}
          </CardContent>
        </Card>
        <StepFooter
          isLoading={mutationIsLoading}
          onClickBack={activeTab > 0 ? (): void => setActiveTab((value) => value - 1) : undefined}
          onClickNext={handleClickNext}
          nextButtonTitle={activeTab === LAST_STEP_INDEX ? 'Save and close' : 'Next'}
        />
      </Content>
    </Layout>
  );
};
export default EditFnaPage;
